import { Link } from 'react-router-dom';

interface ThreadCardProps {
  id: string;
  title: string;
  content: string;
  author: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  isPinned: boolean;
  replyCount: number;
  likeCount: number;
  createdAt: string;
  board: 'feedback' | 'theory' | 'fanart';
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ThreadCard({
  id,
  title,
  content,
  author,
  isPinned,
  replyCount,
  likeCount,
  createdAt,
  board,
  isExpanded,
  onToggleExpand,
}: ThreadCardProps) {
  const previewText = content.length > 150 ? `${content.substring(0, 150)}...` : content;

  return (
    <div className="bg-zinc-900/50 border border-red-900/20 hover:border-red-900/40 transition-all duration-300 group">
      <div 
        className="p-6 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-start gap-4">
          {/* Author Avatar */}
          <Link 
            to={`/profile/writer/${author.id}`} 
            className="flex-shrink-0" 
            onClick={(e) => e.stopPropagation()}
          >
            {author.avatar_url ? (
              <img
                src={author.avatar_url}
                alt={author.username}
                className="w-12 h-12 rounded-full object-cover border border-red-900/30"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-red-900/20 border border-red-900/30 flex items-center justify-center">
                <i className="ri-user-line text-red-500 text-xl"></i>
              </div>
            )}
          </Link>

          {/* Thread Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {isPinned && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-900/20 border border-red-900/30 text-red-400 text-xs font-['Cinzel']">
                  <i className="ri-pushpin-fill text-xs"></i>
                  PINNED
                </span>
              )}
              <Link
                to={`/profile/writer/${author.id}`}
                className="text-sm text-red-400 hover:text-red-300 transition-colors font-['Crimson_Text']"
                onClick={(e) => e.stopPropagation()}
              >
                {author.username}
              </Link>
              <span className="text-zinc-600 text-sm">•</span>
              <span className="text-zinc-500 text-sm font-['Crimson_Text']">{formatDate(createdAt)}</span>
            </div>

            <h3 className="text-lg font-['Cinzel'] text-zinc-100 mb-2 group-hover:text-red-400 transition-colors">
              {title}
            </h3>

            {!isExpanded && (
              <p className="text-zinc-400 text-sm font-['Crimson_Text'] leading-relaxed mb-4">
                {previewText}
              </p>
            )}

            {/* Stats */}
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-chat-3-line text-base"></i>
                <span className="font-['Crimson_Text']">{replyCount} {replyCount === 1 ? 'reply' : 'replies'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-heart-line text-base"></i>
                <span className="font-['Crimson_Text']">{likeCount} {likeCount === 1 ? 'like' : 'likes'}</span>
              </div>
              <div className="ml-auto flex items-center gap-1 text-red-400">
                <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
                <span className="text-xs font-['Cinzel']">{isExpanded ? 'Collapse' : 'Expand'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}